import React, { createContext } from 'react';
import { AnyAction } from '@reduxjs/toolkit';

/**
 * Interface for mouse coordinates
 */
export interface MousePosition {
  x: number;
  y: number;
}

/**
 * Interface for drawing box dimensions
 */
export interface DrawingBox {
  bbox: {
    x0: number;
    y0: number;
    x1: number;
    y1: number;
    cx: number;
    cy: number;
    width: number;
    height: number;
  };
  zoom: boolean;
  scale: number;
}

/**
 * Interface for a device
 */
export interface Device {
  id: string;
  type: string;
  [key: string]: any;
}

/**
 * Interface for an action dispatcher function
 */
export interface ActionDispatcher {
  (
    evt: React.MouseEvent,
    dispatch: (action: AnyAction) => void,
    mouse: MousePosition,
    drawingBox: DrawingBox,
    currentDevice: string | null
  ): void;
}

/**
 * Interface for a rendering element
 */
export interface RenderElement {
  element: string;
  [key: string]: any;
}

/**
 * Interface for a condition
 */
export interface Condition {
  target?: string;
  value?: any;
  [key: string]: any;
}

/**
 * Interface for a modal
 */
export interface Modal {
  header?: string;
  image?: string;
  body?: Array<any>;
  footer?: Array<any>;
  dispatcher?: string | { name: string; value: any };
  theme?: string;
  [key: string]: any;
}

/**
 * Interface for tool constants
 */
export interface ToolConstants {
  tool: {
    containerName?: string;
    landingMenu?: string;
    addAreaLabel?: string;
    [key: string]: any;
  };
  [key: string]: any;
}

/**
 * Interface for the ToolContext
 */
export interface ToolContextType {
  actionDispatchers: Record<string, ActionDispatcher>;
  containerElements?: RenderElement[];
  areaElements?: RenderElement[];
  otherElements?: RenderElement[];
  containerCondition?: Condition;
  areaCondition?: Condition;
  constants: ToolConstants;
  modals: Record<string, Modal>;
  [key: string]: any;
}

/**
 * Create the ToolContext with a default undefined value
 * The actual context value will be provided by a ToolContextProvider component
 */
const ToolContext = createContext<ToolContextType | undefined>(undefined);

export default ToolContext;
